import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Dialog } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { setOnline, setOpen } from "@store/offlineBannerSlice";

const PING_INITIAL_DELAY_MS = 2000;
const PING_MAX_DELAY_MS = 30000;

const getPingUrl = () => {
  const base = process.env.REACT_APP_API_URL;
  return base ? (base.endsWith("/") ? base : `${base}/`) : "";
};

const OfflineBanner: FC = () => {
  const { open, online } = useSelector(
    (state: RootState) => state.offlineBanner,
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isRetrying, setIsRetrying] = useState(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const attemptRef = useRef(0);

  const onConnectionChange = useCallback(() => {
    dispatch(setOnline(navigator.onLine));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setOpen(!online));
  }, [online, dispatch]);

  useEffect(() => {
    const _onConnectionChange = onConnectionChange;

    window.addEventListener("load", _onConnectionChange);
    window.addEventListener("offline", _onConnectionChange);
    window.addEventListener("online", _onConnectionChange);

    return () => {
      window.removeEventListener("load", _onConnectionChange);
      window.removeEventListener("offline", _onConnectionChange);
      window.removeEventListener("online", _onConnectionChange);
    };
  }, [dispatch, onConnectionChange]);

  useEffect(() => {
    if (!open) {
      attemptRef.current = 0;
      return;
    }

    const pingUrl = getPingUrl();
    if (!pingUrl) return;

    const scheduleNext = () => {
      const delay = Math.min(
        PING_INITIAL_DELAY_MS * 2 ** attemptRef.current,
        PING_MAX_DELAY_MS,
      );
      attemptRef.current += 1;
      retryTimeoutRef.current = setTimeout(runPing, delay);
    };

    const runPing = () => {
      retryTimeoutRef.current = null;
      const controller = new AbortController();
      abortControllerRef.current = controller;

      fetch(pingUrl, { method: "GET", signal: controller.signal })
        .then((res) => {
          if (res.ok) {
            dispatch(setOnline(true));
            dispatch(setOpen(false));
          } else {
            scheduleNext();
          }
        })
        .catch(() => {
          scheduleNext();
        })
        .finally(() => {
          if (abortControllerRef.current === controller) {
            abortControllerRef.current = null;
          }
        });
    };

    runPing();

    return () => {
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      attemptRef.current = 0;
    };
  }, [open, dispatch]);

  const retryPing = async () => {
    if (isRetrying) return;

    const pingUrl = getPingUrl();
    if (!pingUrl) return;

    setIsRetrying(true);
    try {
      const res = await fetch(pingUrl, { method: "GET", cache: "no-store" });
      if (res.ok) {
        dispatch(setOnline(true));
        dispatch(setOpen(false));
      }
    } catch {
      // Ignore errors: the background incremental retry loop will keep going while `open` is true.
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
        "& .MuiAlert-action": {
          alignItems: "center",
          padding: 0,
          paddingLeft: "10px",
        },
      }}
    >
      <Alert
        severity="error"
        action={
          <Button
            autoFocus
            variant="outlined"
            sx={{
              whiteSpace: "nowrap",
              color: "#d32f2f",
              borderColor: "#d32f2f",
            }}
            onClick={retryPing}
            disabled={isRetrying}
          >
            {t("offlineBanner.reloadButton")}
          </Button>
        }
        sx={{
          ".MuiAlert-message": {
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        {t("offlineBanner.title")}
      </Alert>
    </Dialog>
  );
};

export default OfflineBanner;
